import React, { useRef, useState } from "react";
import tw from "twin.macro";
import styled, { css } from "styled-components";

const Section = styled.section`
  ${tw`w-full`}
  background: #fab600;
  padding: clamp(3rem, 2.2rem + 2vw, 4.5rem) 1.5rem clamp(4rem, 2.6rem + 3vw, 6rem);
`;

const Inner = tw.div`max-w-4xl mx-auto`;

const Title = tw.h2`text-3xl sm:text-4xl font-semibold tracking-tight text-[#14133B] text-center`;
const Subtitle = tw.p`mt-4 text-base sm:text-lg text-[#14133B] leading-relaxed text-center`;

const Form = tw.form`mt-10 flex flex-col gap-6`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  ${tw`text-sm font-semibold uppercase tracking-[0.18em] text-[#14133B]`}
  display: block;
`;

const Input = styled.input`
  background: rgba(255, 255, 255, 0.7);
  border: 2px solid rgba(20, 19, 59, 0.24);
  border-radius: 18px;
  padding: 12px 16px;
  font-size: 1rem;
  color: #14133b;
  transition: border-color 0.2s ease, transform 0.2s ease;

  &::placeholder {
    color: rgba(20, 19, 59, 0.7);
  }

  &:focus {
    outline: none;
    border-color: #14133b;
    transform: translateY(-1px);
  }
`;

const UploadZone = styled.label(({ $isDragging }) => css`
  ${tw`flex flex-col items-center justify-center text-center cursor-pointer`}
  border: 2px dashed rgba(20, 19, 59, 0.35);
  border-radius: 24px;
  padding: clamp(2.8rem, 2.2rem + 1.8vw, 4rem);
  background: ${$isDragging ? "rgba(255, 251, 220, 0.6)" : "rgba(255, 255, 255, 0.55)"};
  color: #14133b;
  transition: border-color 0.2s ease, background 0.2s ease, transform 0.2s ease;

  &:hover {
    border-color: #14133b;
    transform: translateY(-1px);
  }

  strong { ${tw`block text-lg sm:text-xl font-semibold mt-2`} }
  span { ${tw`text-sm sm:text-base mt-1`} }
`);

const HiddenFileInput = styled.input`
  display: none;
`;

const SubmitButton = styled.button`
  ${tw`inline-flex items-center justify-center uppercase font-semibold self-center`}
  margin-top: 1.5rem;
  padding: 0.9rem 3rem;
  border-radius: 999px;
  background: #14133b !important;
  color: #fffbdc;
  border: none;
  cursor: pointer;
  letter-spacing: 0.12em;
  transition: transform 0.18s ease, box-shadow 0.18s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 16px 28px rgba(20, 19, 65, 0.18);
  }

  &:active {
    transform: translateY(0);
    box-shadow: none;
  }
`;

const HelperText = tw.p`text-sm text-center text-[#14133B] mt-3`;
const ErrorText = tw.span`text-xs text-[#E11D48] mt-1 text-left`;

const CareerUploadForm = React.forwardRef((_, ref) => {
  const [values, setValues] = useState({
    name: "",
    email: "",
    phone: "",
    role: "",
  });
  const [fileName, setFileName] = useState("");
  const [fileObject, setFileObject] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const fileInputRef = useRef(null);
  const fieldRefs = useRef({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    setValues((prev) => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setFileName(file.name);
      setFileObject(file);
      if (fieldErrors.cv) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.cv;
          return next;
        });
      }
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) {
      setFileName(file.name);
      setFileObject(file);
      if (fileInputRef.current) {
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        fileInputRef.current.files = dataTransfer.files;
      }
      if (fieldErrors.cv) {
        setFieldErrors((prev) => {
          const next = { ...prev };
          delete next.cv;
          return next;
        });
      }
    }
  };

  const preventDefaults = (event) => {
    event.preventDefault();
    if (event.type === "dragover") {
      setIsDragging(true);
    }
    if (event.type === "dragleave") {
      setIsDragging(false);
    }
  };

  const validate = () => {
    const errors = {};
    if (!values.name.trim()) errors.name = "Full name is required.";
    if (!values.email.trim()) {
      errors.email = "Email is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = "Please enter a valid email address.";
    }
    if (!values.phone.trim()) errors.phone = "Phone number is required.";
    if (!fileObject) errors.cv = "CV attachment is required.";
    return errors;
  };

  const scrollToField = (fieldName) => {
    const node = fieldRefs.current[fieldName];
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "center" });
      node.querySelector("input, textarea")?.focus();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitMessage(null);
    setSubmitError(null);

    const errors = validate();
    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return;
    }

    try {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append("name", values.name.trim());
      formData.append("email", values.email.trim());
      formData.append("phone", values.phone.trim());
      formData.append("role", values.role.trim());
      if (fileObject) {
        formData.append("career-cv", fileObject);
      }

      const response = await fetch("/api/SendCareer/", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        const message = data?.message || "Unable to submit request.";
        const serverErrors = data?.errors || {};
        setSubmitError(message);
        setFieldErrors((prev) => ({ ...prev, ...serverErrors }));
        const firstServerError = Object.keys(serverErrors)[0];
        if (firstServerError) {
          scrollToField(firstServerError === "cv" ? "cv" : firstServerError);
        }
        return;
      }

      setSubmitMessage("Thanks! Your application has been received.");
      setValues({ name: "", email: "", phone: "", role: "" });
      setFileName("");
      setFileObject(null);
      setFieldErrors({});
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      setSubmitError("Submission failed. Please try again or email us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Section ref={ref} id="careers-upload">
      <Inner>
        <Title>Upload Your CV</Title>
        <Subtitle>
          Tell us about yourself and attach your CV. Our people team reviews every submission and will reach out if you're a match.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          <Field ref={(node) => (fieldRefs.current.name = node)}>
            <Label htmlFor="career-name">Full Name*</Label>
            <Input
              id="career-name"
              name="name"
              placeholder="Enter your full name"
              value={values.name}
              onChange={handleChange}
              aria-invalid={fieldErrors.name ? "true" : "false"}
            />
            {fieldErrors.name && <ErrorText>{fieldErrors.name}</ErrorText>}
          </Field>

          <Field ref={(node) => (fieldRefs.current.email = node)}>
            <Label htmlFor="career-email">Email*</Label>
            <Input
              id="career-email"
              type="email"
              name="email"
              placeholder="name@example.com"
              value={values.email}
              onChange={handleChange}
              aria-invalid={fieldErrors.email ? "true" : "false"}
            />
            {fieldErrors.email && <ErrorText>{fieldErrors.email}</ErrorText>}
          </Field>

          <Field ref={(node) => (fieldRefs.current.phone = node)}>
            <Label htmlFor="career-phone">Phone*</Label>
            <Input
              id="career-phone"
              type="tel"
              name="phone"
              placeholder="Preferred contact number"
              value={values.phone}
              onChange={handleChange}
              aria-invalid={fieldErrors.phone ? "true" : "false"}
            />
            {fieldErrors.phone && <ErrorText>{fieldErrors.phone}</ErrorText>}
          </Field>

          <Field ref={(node) => (fieldRefs.current.role = node)}>
            <Label htmlFor="career-role">Preferred Role / Department</Label>
            <Input
              id="career-role"
              name="role"
              placeholder="Kitchen, operations, marketing..."
              value={values.role}
              onChange={handleChange}
            />
          </Field>

          <Field ref={(node) => (fieldRefs.current.cv = node)}>
            <Label>Attach CV*</Label>
            <UploadZone
              $isDragging={isDragging}
              htmlFor="career-cv"
              onDragOver={preventDefaults}
              onDragEnter={preventDefaults}
              onDragLeave={preventDefaults}
              onDrop={handleDrop}
            >
              <HiddenFileInput
                id="career-cv"
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                ref={fileInputRef}
                onChange={handleFileChange}
              />
              <strong>{fileName || "Drag & Drop your CV"}</strong>
              <span>or click to browse files (PDF, DOC, DOCX)</span>
            </UploadZone>
            {fieldErrors.cv && <ErrorText>{fieldErrors.cv}</ErrorText>}
          </Field>

          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Submitting…" : "Submit"}
          </SubmitButton>
          <HelperText>*Required fields</HelperText>
          {submitMessage && <HelperText css={[tw`text-[#2F855A]`]}>{submitMessage}</HelperText>}
          {submitError && <HelperText css={[tw`text-[#E53E3E]`]}>{submitError}</HelperText>}
        </Form>
      </Inner>
    </Section>
  );
});

export default CareerUploadForm;
